/**
 * UI-5b Task 7 — voicemails Zustand store.
 *
 * Holds the current agent's voicemails. `useMyVoicemails` fetches and
 * writes the list here. The `voicemail:new` socket handler triggers a full
 * refetch (the event payload lacks `recordingId`/`transcript`) and replaces
 * the list via `setMyVoicemails`. Consumers subscribe to `myVoicemails` so
 * both poll-driven and socket-driven updates reach the UI.
 */
import { create } from 'zustand'

export interface Voicemail {
  id: string
  callId: string
  agentId: string | null
  recordingId: string
  durationMs: number
  status: 'NEW' | 'HEARD' | 'DELETED'
  transcript: string | null
  createdAt: string
  /** Caller display name — available when the voicemail box has caller ID. */
  callerName?: string | null
  /** Caller phone number in E.164 or national format. */
  callerNumber?: string | null
  /** Direct audio URL for playback (used by demo/dummy data). When absent, the
   *  row falls back to the RecordingMiniPlayer's `/api/recordings/:id/stream` fetch. */
  audioUrl?: string | null | undefined
}

interface VoicemailsStoreState {
  myVoicemails: Voicemail[]
  setMyVoicemails: (vms: Voicemail[]) => void
  /** Prepend a voicemail. Idempotent — a voicemail already in the list is not double-added. */
  prependVoicemail: (vm: Voicemail) => void
  patchVoicemail: (id: string, patch: Partial<Voicemail>) => void
  removeVoicemail: (id: string) => void
  reset: () => void
}

export const useVoicemailsStore = create<VoicemailsStoreState>()((set) => ({
  myVoicemails: [],
  setMyVoicemails: (myVoicemails) => set({ myVoicemails }),
  prependVoicemail: (vm) =>
    set((state) =>
      state.myVoicemails.some((v) => v.id === vm.id)
        ? state
        : { myVoicemails: [vm, ...state.myVoicemails] },
    ),
  removeVoicemail: (id) =>
    set((state) => ({
      myVoicemails: state.myVoicemails.filter((v) => v.id !== id),
    })),
  patchVoicemail: (id, patch) =>
    set((state) => ({
      myVoicemails: state.myVoicemails.map((v) =>
        v.id === id ? { ...v, ...patch } : v,
      ),
    })),
  reset: () => set({ myVoicemails: [] }),
}))
